/**
 * SignupPage — profile creation wizard.
 *
 * Reached after OTP verify when the backend says `isNewUser: true`.
 * Routing: AppRouter mounts this under `/signup` as a ProtectedRoute,
 * so by the time we render, an access token is already in Redux.
 *
 * Layout — 3 steps:
 *   0) Personal info  — firstName, lastName, age, gender
 *   1) Address & health — address, chronicDiseases, drugAllergies, foodAllergies
 *   2) Review & submit — read-only summary + Submit button
 *
 * History:
 *   - Session 4: built as a monolithic file with everything inline.
 *   - Session 5 follow-up: autoSelect Autocomplete fix added.
 *   - Session 6: extracted the field sections and validation schema into
 *     `src/components/profile/` so ProfilePage can share them. This file
 *     is now wizard-orchestration only — step navigation, the review
 *     screen, and the submit handler. Field rendering and validation
 *     are owned by the shared modules.
 *
 * Form mechanics:
 *   - ONE useForm instance with all 8 user-entered fields. We validate
 *     ONLY the current step's fields when the user clicks "Next" by
 *     calling react-hook-form's `trigger(fieldNames)`.
 *   - zodResolver runs the whole schema on each `trigger()` call, even
 *     when names are passed — but only reports errors for the listed
 *     names. After a successful advance we `clearErrors()` so the next
 *     step renders clean (otherwise its untouched fields would appear
 *     pre-failed). Documented inline at handleNext.
 *   - `language` is NOT asked here — it's already chosen via the
 *     LanguageToggle and read from Redux at submit time.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Paper,
  Stack,
  Typography,
  Button,
  Alert,
  Box,
  Stepper,
  Step,
  StepLabel,
  Divider,
} from '@mui/material';
import Container from '@/components/PageSheet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LogoutIcon from '@mui/icons-material/Logout';

import LanguageToggle from '@/components/LanguageToggle';
import PersonalSection from '@/components/profile/PersonalSection';
import HealthSection from '@/components/profile/HealthSection';
import { buildProfileSchema } from '@/components/profile/schema';
import { getDiseaseLabel } from '@/components/profile/constants';
import { userApi, authApi } from '@/services/api';
import { setUser, logout, setProfileLoaded } from '@/store/authSlice';
import { selectLanguage, selectIsRtl } from '@/store/uiSlice';

/**
 * Step definitions. `fields` is the subset of the schema that we run
 * `trigger()` against when the user clicks Next on that step.
 */
const STEPS = [
  {
    id: 'personal',
    labelKey: 'signup.steps.personal',
    fields: ['firstName', 'lastName', 'age', 'gender'],
  },
  {
    id: 'health',
    labelKey: 'signup.steps.health',
    fields: ['address', 'chronicDiseases', 'drugAllergies', 'foodAllergies'],
  },
  {
    id: 'review',
    labelKey: 'signup.steps.review',
    fields: [],
  },
];

/* ------------------------------------------------------------------ */
/* Review section — signup-specific (not extracted to /components)    */
/* ------------------------------------------------------------------ */

/**
 * Read-only summary shown on the final wizard step. Lets the user
 * verify everything before submitting health data. Lives here rather
 * than in shared components because there's no edit-page equivalent.
 */
function ReviewSection({ values, language, t }) {
  /** Render a labeled line; if the value is empty, show "—". */
  const row = (label, value) => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={2}
      sx={{ py: 0.5 }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ textAlign: 'end', wordBreak: 'break-word' }}
      >
        {value || '—'}
      </Typography>
    </Stack>
  );

  const listOrDash = (arr, translateOption) => {
    if (!arr || arr.length === 0) return t('signup.review.none');
    return arr
      .map((item) => (translateOption ? translateOption(item) : item))
      .join('، ');
  };

  return (
    <Stack spacing={1.5}>
      <Alert severity="info" icon={false}>
        {t('signup.review.subtitle')}
      </Alert>

      <Box>
        <Typography variant="overline" color="text.secondary">
          {t('signup.steps.personal')}
        </Typography>
        {row(t('signup.fields.firstName.label'), values.firstName)}
        {row(t('signup.fields.lastName.label'), values.lastName)}
        {row(t('signup.fields.age.label'), values.age)}
        {row(
          t('signup.fields.gender.label'),
          values.gender ? t(`signup.fields.gender.${values.gender}`) : ''
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="overline" color="text.secondary">
          {t('signup.steps.health')}
        </Typography>
        {row(t('signup.fields.address.label'), values.address)}
        {row(
          t('signup.fields.chronicDiseases.label'),
          listOrDash(values.chronicDiseases, (c) => getDiseaseLabel(c, t))
        )}
        {row(
          t('signup.fields.drugAllergies.label'),
          listOrDash(values.drugAllergies)
        )}
        {row(
          t('signup.fields.foodAllergies.label'),
          listOrDash(values.foodAllergies)
        )}
      </Box>

      <Divider />

      <Box>
        {row(
          t('signup.review.language'),
          language === 'fa' ? t('language.persian') : t('language.english')
        )}
      </Box>
    </Stack>
  );
}

/* ------------------------------------------------------------------ */
/* Main component                                                     */
/* ------------------------------------------------------------------ */

export function SignupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const language = useSelector(selectLanguage);
  const isRtl = useSelector(selectIsRtl);

  const [currentStep, setCurrentStep] = useState(0);
  const [serverError, setServerError] = useState(null);

  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(buildProfileSchema(t)),
    defaultValues: {
      firstName: '',
      lastName: '',
      age: '',
      gender: '',
      address: '',
      chronicDiseases: [],
      drugAllergies: [],
      foodAllergies: [],
    },
    mode: 'onSubmit',
  });

  /**
   * Advance to the next step if the current step's fields validate.
   *
   * zodResolver+trigger gotcha: even when we pass specific field names
   * to `trigger()`, the resolver runs the WHOLE schema. So `errors`
   * gets populated for every invalid field — including step 2 fields
   * the user hasn't touched yet. We `clearErrors()` after a successful
   * advance so the next step renders clean and only errors after the
   * user acts on it.
   */
  const handleNext = async () => {
    setServerError(null);
    const fieldsToValidate = STEPS[currentStep].fields;
    const ok = await trigger(fieldsToValidate);
    if (ok) {
      clearErrors();
      setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
    }
  };

  const handleBack = () => {
    setServerError(null);
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  /**
   * Final submission — only fires from the Review step.
   * `data` here is the FULL form payload, already validated & coerced
   * by Zod (e.g. `age` is a number, arrays are deduped).
   */
  const onSubmit = async (data) => {
    setServerError(null);
    const payload = { ...data, language };

    try {
      const user = await userApi.signup(payload);
      // The profile now exists, and we know it.
      dispatch(setUser(user));
      dispatch(setProfileLoaded(true));

      // A guest sent here mid-consultation must land back where they left off.
      // Dropping them on /welcome abandoned a consultation they had already
      // captured for, with no way back to choosing a doctor — the flow simply
      // ended. `next` is only honoured when it is a path on this app, so it
      // cannot be used to bounce someone to another origin.
      const next = searchParams.get('next');
      const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : null;

      navigate(safeNext || '/welcome', { replace: true });
    } catch (err) {
      handleApiError(err);
    }
  };

  /** Map API errors to the inline alert. */
  const handleApiError = (err) => {
    const status = err?.response?.status;
    if (!err?.response) {
      setServerError(t('auth.networkError'));
      return;
    }
    if (status === 400) {
      // Backend rejected validation we didn't catch. Bounce user to step 0
      // so they can review everything; show the generic message.
      setServerError(t('signup.errorValidation'));
      setCurrentStep(0);
      return;
    }
    if (status === 409) {
      setServerError(t('signup.errorConflict'));
      return;
    }
    setServerError(t('errors.serverError'));
  };

  /**
   * Logout escape hatch — useful for testing the new-user flow
   * repeatedly with mocks. Not the primary flow; rendered small/dim.
   */
  const handleLogout = async () => {
    try {
      await authApi.logoutRequest();
    } catch (err) {
      console.warn('Logout request failed:', err);
    }
    dispatch(logout());
    navigate('/login', { replace: true });
  };

  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>
        {/* Header row: title + language toggle */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <PersonAddIcon color="primary" />
            <Typography variant="h5" component="h1" fontWeight={700}>
              {t('signup.title')}
            </Typography>
          </Stack>
          <LanguageToggle />
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {t('signup.subtitle')}
        </Typography>

        {/* Progress indicator — MUI Stepper is RTL-aware via the theme */}
        <Stepper activeStep={currentStep} alternativeLabel>
          {STEPS.map((step) => (
            <Step key={step.id}>
              <StepLabel>{t(step.labelKey)}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {currentStep === 0 && (
              <PersonalSection control={control} errors={errors} t={t} />
            )}
            {currentStep === 1 && (
              <HealthSection control={control} errors={errors} t={t} />
            )}
            {currentStep === 2 && (
              <ReviewSection values={getValues()} language={language} t={t} />
            )}

            {serverError && (
              <Alert
                severity="error"
                onClose={() => setServerError(null)}
                sx={{ mt: 2 }}
              >
                {serverError}
              </Alert>
            )}

            {/* Back/Next/Submit row. In RTL, the arrow icons flip so
                "Back" still points visually backward. */}
            <Stack
              direction="row"
              justifyContent="space-between"
              spacing={2}
              sx={{ mt: 3 }}
            >
              <Button
                type="button"
                onClick={handleBack}
                disabled={currentStep === 0 || isSubmitting}
                startIcon={isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
              >
                {t('common.back')}
              </Button>

              {isLastStep ? (
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  startIcon={<CheckIcon />}
                >
                  {isSubmitting ? t('signup.submitting') : t('signup.submit')}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="contained"
                  size="large"
                  onClick={handleNext}
                  endIcon={isRtl ? <ArrowBackIcon /> : <ArrowForwardIcon />}
                >
                  {t('common.next')}
                </Button>
              )}
            </Stack>
          </form>
        </Paper>

        {/* Logout escape hatch — small and low-contrast */}
        <Box display="flex" justifyContent="center">
          <Button
            size="small"
            color="inherit"
            startIcon={<LogoutIcon fontSize="small" />}
            onClick={handleLogout}
            sx={{ opacity: 0.6 }}
          >
            {t('common.logout')}
          </Button>
        </Box>
      </Stack>
    </Container>
  );
}

export default SignupPage;
